import { defineStore, acceptHMRUpdate } from "pinia";
import { brogglBack } from "../axios.js";
import { personalReportTypes } from "../assets/variables.js";
import { useUserStore } from "./user.js";
import dayjs from "dayjs";
import router from "../router/index.js";

export const usePersonalReportStore = defineStore("personalReport", {
  state: () => ({
    total: 0,
    chart: {},
    barChartKey: 0,
    empty: false,
    chartData: {
      labels: [],
      datasets: [],
    },
    parameters: {
      breakdown: "Month",
      period: "Last Quarter",
      personalReportType: personalReportTypes[0].value,
      offset: 1,
    },
    totals: {},
    details: {},
    favoritesPages: [],
    isLead: false,
  }),
  getters: {
    getTotal: (state) => state.total,
    getChart: (state) => state.chart,
    getParameters: (state) => state.parameters,
    getBarChartKey: (state) => state.barChartKey,
    getChartData: (state) => state.chartData,
    getEmpty: (state) => state.empty,
    getTotals: (state) => state.totals,
    getDetails: (state) => state.details,
    getFavoritesPages: (state) => state.favoritesPages,
    getIsLead: (state) => state.isLead,
  },
  actions: {
    async getReport() {
      this.chart = {};
      this.chartData = {
        labels: [],
        datasets: [],
      };
      this.total = 0;
      this.empty = false;
      let userId = useUserStore().getUserId();
      if (this.parameters.personalReportType === "project") {
        const res = await brogglBack.get("/report/user/project-report", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            period: this.parameters.period,
            breakdown: this.parameters.breakdown,
            userId: userId,
          },
        });
        let sumCS = [];
        let sumPS = [];
        let sumOnBoarding = [];
        let sumOtherProjects = [];
        for (let i = 0; i < res.data.data.length; i++) {
          this.total += res.data.data[i].sumAllProjects;
          sumCS.push(res.data.data[i].sumCS);
          sumPS.push(res.data.data[i].sumPS);
          sumOnBoarding.push(res.data.data[i].sumOnBoarding);
          sumOtherProjects.push(res.data.data[i].sumOtherProjects);
        }
        this.chartData.datasets.push({
          label: "CS",
          backgroundColor: "#217981",
          borderColor: "#000000",
          data: sumCS,
        });
        this.chartData.datasets.push({
          label: "PS",
          backgroundColor: "#4ca2cd",
          borderColor: "#000000",
          data: sumPS,
        });
        this.chartData.datasets.push({
          label: "OnBoarding",
          backgroundColor: "#4ddbb8",
          borderColor: "#000000",
          data: sumOnBoarding,
        });
        this.chartData.datasets.push({
          label: "Other Projects",
          backgroundColor: "#48d4d4",
          borderColor: "#000000",
          data: sumOtherProjects,
        });

        this.chartData.labels = res.data.data.map((item) =>
          dayjs(item.breakdown).format("MMM-DD-YYYY"),
        );
        this.chart = res.data.data;
      } else if (this.parameters.personalReportType === "billable") {
        const res = await brogglBack.get("/report/user/BillableReport", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            breakdown: this.parameters.breakdown,
            period: this.parameters.period,
            userId: userId,
          },
        });
        let sumBillable = [];
        let sumNonBillable = [];
        let sumInternal = [];
        for (let i = 0; i < res.data.data.length; i++) {
          this.total += res.data.data[i].sumAll;
          sumBillable.push(res.data.data[i].sumBillable);
          sumNonBillable.push(res.data.data[i].sumNonBillable);
          sumInternal.push(res.data.data[i].sumInternal);
        }
        this.chartData.datasets.push({
          label: "Billable",
          backgroundColor: "#217981",
          borderColor: "#000000",
          fill: false,
          data: sumBillable,
        });
        this.chartData.datasets.push({
          label: "Non Billable",
          backgroundColor: "#4ca2cd",
          borderColor: "#000000",
          fill: false,
          data: sumNonBillable,
        });
        this.chartData.datasets.push({
          label: "Internal",
          backgroundColor: "#4ddbb8",
          borderColor: "#000000",
          fill: false,
          data: sumInternal,
        });

        this.chartData.labels = res.data.data.map((item) =>
          dayjs(item.breakdown).format("MMM-DD-YYYY"),
        );
        this.chart = res.data.data;
      } else {
        await this.fetchFavoritesPages();
        if (this.favoritesPages.length > 0) {
          const res = await brogglBack.get("/report/user/ClientReport", {
            headers: {
              Authorization: "Bearer " + window.$cookies.get("jwt"),
            },
            params: {
              period: this.parameters.period,
              userId: userId,
              offset: this.parameters.offset,
            },
          });
          if (res.data.data !== null) {
            var dataset = [];
            res.data.data.forEach((item) => {
              this.total += item.sum;
              dataset.push(item.sum);
            });
            this.chartData.datasets.push({
              label: "Client",
              backgroundColor: "#217981",
              borderColor: "#000000",
              fill: false,
              data: dataset,
            });
            this.chartData.labels = res.data.data.map((item) => item.name);
            this.chart = res.data.data;
          }
        }
      }
      this.barChartKey++;
      this.empty = this.chartData.datasets.every(
        (dataset) => dataset.data.length === 0,
      );
      this.total = this.total.toLocaleString();
    },
    async fetchFavoritesPages() {
      let userId = useUserStore().getUserId();
      const resFavorites = await brogglBack.get("/favorites", {
        headers: {
          Authorization: "Bearer " + window.$cookies.get("jwt"),
        },
        params: {
          userId: userId,
        },
      });
      for (
        let i = 1;
        i <= Object.keys(resFavorites.data.data).length / 15 + 1;
        i++
      ) {
        this.favoritesPages.push(i);
      }
    },
    calculateTotals() {
      this.totals = {};
      if (this.parameters.personalReportType === "project") {
        this.totals.CS = this.chart.reduce((acc, curr) => acc + curr.sumCS, 0);
        this.totals.PS = this.chart.reduce((acc, curr) => acc + curr.sumPS, 0);
        this.totals.OnBoarding = this.chart.reduce(
          (acc, curr) => acc + curr.sumOnBoarding,
          0,
        );
        this.totals.OtherProjects = this.chart.reduce(
          (acc, curr) => acc + curr.sumOtherProjects,
          0,
        );
      } else if (this.parameters.personalReportType === "billable") {
        this.totals.Billable = this.chart.reduce(
          (acc, curr) => acc + curr.sumBillable,
          0,
        );
        this.totals.NonBillable = this.chart.reduce(
          (acc, curr) => acc + curr.sumNonBillable,
          0,
        );
        this.totals.Internal = this.chart.reduce(
          (acc, curr) => acc + curr.sumInternal,
          0,
        );
      }
      Object.keys(this.totals).forEach((total) => {
        total = total.toLocaleString();
      });
    },
    convertArrayOfObjectsToCSV(args) {
      var result, columnDelimiter, lineDelimiter, data;
      data = args.data;
      columnDelimiter = ",";
      lineDelimiter = "\n";
      result = "";
      let keys = Object.keys(data[0]);
      result += keys.join(columnDelimiter) + lineDelimiter;
      data.forEach(function (item) {
        Object.keys(item).forEach((key) => {
          result += item[key] + columnDelimiter;
        });
        result += lineDelimiter;
      });
      return result;
    },
    csvExport(arrData) {
      var data, filename, link;
      var csv = this.convertArrayOfObjectsToCSV({
        data: arrData,
      });
      if (csv == null) return;
      filename = arrData.filename || "export.csv";
      if (!csv.match(/^data:text\/csv/i)) {
        csv = "data:text/csv;charset=utf-8," + csv;
      }
      data = encodeURI(csv);
      link = document.createElement("a");
      link.setAttribute("href", data);
      link.setAttribute("download", filename);
      link.click();
    },
    async IsLead() {
      let userId = useUserStore().getUserId();
      const res = await brogglBack.get("/user", {
        headers: {
          Authorization: "Bearer " + window.$cookies.get("jwt"),
        },
        params: {
          id: userId,
        },
      });
      this.isLead = res.data.data.IsLead;
    },
    redirect() {
      router.push("/team-lead-report");
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(usePersonalReportStore, import.meta.hot),
  );
}
